import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #f0f2f5;
`;

const LoginBox = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
`;

const Button = styled.button`
  width: 100%;
  padding: 10px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  &:hover { background-color: #004a94; }
  &:disabled { background-color: #ccc; }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #005eb8;
  text-decoration: underline;
  cursor: pointer;
  margin-top: 1rem;
  font-size: 0.9rem;
  width: 100%;
  &:hover { color: #004a94; }
`;

const AdminLogin = () => {
  const [mode, setMode] = useState('login'); // 'login', 'forgot', 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/admins/login`, {
        email,
        password
      });

      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('adminName', response.data.name);
        toast.success('Login successful');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/admins/request-reset-code`, { email });
      toast.success('Reset code sent to your email');
      setMode('reset');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/admins/reset-password`, {
        email,
        code,
        newPassword
      });
      toast.success('Password reset successful. Please login.');
      setMode('login');
      setPassword('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <LoginBox>
        <Title>
          {mode === 'login' ? 'Admin Portal' : mode === 'forgot' ? 'Reset Password' : 'Enter Reset Code'}
        </Title>

        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            <Input
              type="email"
              placeholder="Admin Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            <Button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <LinkButton type="button" onClick={() => setMode('forgot')}>Forgot password?</LinkButton>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleRequestReset}>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
              Enter your email address and we'll send you a code to reset your password.
            </p>
            <Input
              type="email"
              placeholder="Admin Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <Button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </Button>
            <LinkButton type="button" onClick={() => setMode('login')}>Back to Login</LinkButton>
          </form>
        )}

        {mode === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <Input
              type="text"
              placeholder="Reset Code"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              required
            />
            <Input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
            />
            <Button type="submit" disabled={loading}>
              {loading ? 'Resetting...' : 'Update Password'}
            </Button>
            <LinkButton type="button" onClick={() => setMode('forgot')}>Resend Code</LinkButton>
          </form>
        )}
      </LoginBox>
    </Container>
  );
};

export default AdminLogin;
